import * as React from 'react';

import { useReducedMotion } from './use-reduced-motion';

/**
 * `static` — never animate; draw it plainly. The starting value, and where a
 * visitor on reduced motion or a browser without `IntersectionObserver` stays.
 *
 * `armed` — held at the start of the animation, waiting to be scrolled to.
 *
 * `revealed` — playing, or finished.
 */
export type RevealState = 'static' | 'armed' | 'revealed';

/**
 * Reveal-on-scroll, arranged so that content is never hidden by a script that
 * might not run.
 *
 * The order is the whole point. The usual way round — hide in CSS, show from
 * JavaScript — means a failed chunk, a thrown error in an effect above this
 * one, or a browser without the observer leaves the page blank in a way nobody
 * sees in testing, because in testing the script always loads. So the starting
 * state here is `static`: fully drawn, no animation. Hiding is something this
 * hook does only once it has an observer in hand and knows it can undo it.
 *
 * That arming happens in a layout effect rather than an ordinary one, and that
 * is not a preference. `useEffect` runs after the browser has painted, so the
 * rows would appear, vanish, and animate back in — a flash on every load.
 * `useLayoutEffect` runs before the paint, so the first frame is already the
 * hidden one.
 *
 * Reveals once. A row that scrolls back out of view and in again has been read;
 * playing it a second time is the page repeating itself.
 */
export function useReveal<T extends Element>(
  /**
   * How far into the viewport the element must come before it counts.
   *
   * The default pulls the bottom edge up by a tenth, so a list starts its
   * animation once it is properly on screen rather than the instant its top
   * pixel crosses the fold — where the first row would finish animating while
   * still half-hidden by the edge.
   */
  rootMargin = '0px 0px -10% 0px',
) {
  const ref = React.useRef<T>(null);
  const reducedMotion = useReducedMotion();
  const [state, setState] = React.useState<RevealState>('static');

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Either of these means the animation is not going to happen, so the
    // content must stay exactly as it is rather than being armed and stranded.
    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      setState('static');
      return;
    }

    setState('armed');

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setState('revealed');
        observer.disconnect();
      }
    }, { rootMargin });

    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion, rootMargin]);

  return { ref, state };
}
